/**
 * Group / wizard-step navigation as seen from the host page.
 *
 * The navigation itself lives in `useGroupNavigator` (it owns the active
 * group and the step validation). That hook registers a controller per
 * instance here; the public instance methods (`getActiveGroup`,
 * `setActiveGroup`, `nextGroup`, …) delegate to it, so a click on the
 * built-in Next button and a host call run through the same gate, guard and
 * `group` event.
 */

/** A group as the host addresses it: numeric id, internal name, position among the visible groups. */
export interface GroupRef {
    id: number;
    /** The group's internal name (`alt_title`), null when the merchant set none. */
    key: string | null;
    /** Zero-based position among the currently visible groups. */
    index: number;
}

export interface GroupInfo extends GroupRef {
    title: string;
    /**
     * Internal names of the group's Characteristics, in display order — the
     * keys under which they appear in `getAssetPayload()`. Rule-hidden
     * Characteristics stay listed (the payload carries them blank with
     * `visibility: 0`); ones without an internal name are left out.
     */
    characteristics: string[];
}

export interface ActiveGroup extends GroupRef {
    /** Number of currently visible groups. */
    total: number;
}

export type StepDirection = 'forward' | 'back' | 'jump';

/** Payload of `on('group', …)`. */
export interface GroupChangeEvent {
    /** The group shown before; null never happens after the first display, kept for type safety. */
    from: GroupRef | null;
    to: GroupRef;
    direction: StepDirection;
}

/** Messages a step guard wants shown at a Characteristic (id or internal name). */
export type StepGuardErrors = ReadonlyArray<{ characteristic: number | string; message: string }>;

export type StepGuardResult = boolean | void | StepGuardErrors;

/**
 * Host check that runs before a step change, after the built-in validation
 * passed. Return `false` or a list of errors to stay on the current step.
 */
export type StepGuard = (ctx: {
    from: GroupRef;
    to: GroupRef;
    direction: StepDirection;
}) => StepGuardResult | Promise<StepGuardResult>;

export interface StepGuardOptions {
    /** Also ask the guard before going back. Default: forward only. */
    backward?: boolean;
}

export interface GroupNavigationController {
    getGroups(): GroupInfo[];
    getActive(): ActiveGroup | null;
    /** Resolves true when the step changed (or already was the target). */
    goTo(ref: number | string, opts: { validate: boolean }): Promise<boolean>;
    next(): Promise<boolean>;
    prev(): Promise<boolean>;
}

/** Fallback while no group navigation is mounted (loading, single group, `no_groups`). */
export const NO_NAVIGATION: GroupNavigationController = {
    getGroups: () => [],
    getActive: () => null,
    goTo: () => Promise.resolve(false),
    next: () => Promise.resolve(false),
    prev: () => Promise.resolve(false),
};
